import { fetchAllEnvelopes, fetchSingleEnvelope, showErrorAlert, showSuccessAlert } from 'actions';
import InvestigationSelect from 'components/Investigations/InvestigationSelect';
import SimpleModal from 'components/SimpleModal';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import useAddEnvelopesToInvestigation from 'hooks/investigations/useAddEnvelopesToInvestigation';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getTree } from 'selectors/envelopes';
import { getNavParamsByResourceMemo } from 'selectors/nav';
import { useSelector } from 'store';
import type { RouteParams } from 'types';

type ComponentProps = {
  isOpen: boolean;
  onClose: () => void;
  envelopeUuid?: string;
};

const AddToInvestigationModal: React.FC<ComponentProps> = ({ isOpen, onClose, envelopeUuid }) => {
  const dispatch = useDispatch();
  const navParams = useSelector((state) =>
    getNavParamsByResourceMemo(state, resourceQueryParamName.envelopes)
  ) as RouteParams;
  const tree = useSelector(getTree);

  const [selectedUuid, setSelectedUuid] = useState('');
  const { addByEnvelopeUuid, addBySearch, isSubmitting } = useAddEnvelopesToInvestigation();

  useEffect(() => {
    if (isOpen) {
      setSelectedUuid('');
    }
  }, [isOpen]);

  const handleSubmit = async (): Promise<void> => {
    if (!selectedUuid) {
      dispatch(showErrorAlert('Select an investigation'));
      return;
    }

    const outcome = envelopeUuid
      ? await addByEnvelopeUuid(selectedUuid, envelopeUuid)
      : await addBySearch(selectedUuid, navParams, tree);

    if (outcome.isError) {
      dispatch(showErrorAlert(outcome.message));
      return;
    }

    dispatch(showSuccessAlert(outcome.message));

    if (outcome.refreshEnvelopes) {
      if (envelopeUuid) {
        dispatch(fetchSingleEnvelope({ envelopeId: envelopeUuid }));
      } else {
        dispatch(fetchAllEnvelopes());
      }
    }

    if (outcome.closeModal) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <SimpleModal title="Add to investigation" toggle={onClose}>
      <p className="text-sm text-litlingo-gray-5 mb-4 mt-4">
        {envelopeUuid
          ? 'Add this message to an investigation.'
          : 'Add all messages that match your current Explore filters to an investigation.'}
      </p>
      <div className="mb-4">
        <InvestigationSelect
          value={selectedUuid}
          onChange={setSelectedUuid}
          enabled={isOpen}
          disabled={isSubmitting}
          dataTestid="add-to-investigation-select"
        />
      </div>
      <div className="flex flex-row justify-end gap-2">
        <button
          type="button"
          className="button button--secondary"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="button button--primary"
          onClick={(): void => {
            handleSubmit().catch(() => undefined);
          }}
          disabled={isSubmitting || !selectedUuid}
        >
          {isSubmitting ? 'Adding…' : 'Add'}
        </button>
      </div>
    </SimpleModal>
  );
};

export default AddToInvestigationModal;
